import { contentLocales } from '@/helpers/localeHelper'
import type { LocaleObject } from '@/types'
import { createSharedComposable } from '@vueuse/core'
import { computed } from 'vue'
import { useCurrentCompanySettings } from './swr/useCurrentCompany'

const formattedLocaleObject = <K extends string, V extends string>(
  locale: LocaleObject,
  keyName: K,
  valueName: V
) => {
  return {
    [keyName]: locale.name,
    [valueName]: locale.code,
  } as Record<K, string> & Record<V, string>
}

const mapLocaleOptions = <K extends string, V extends string>(
  locales: LocaleObject[],
  keyName: K,
  valueName: V
) => {
  return locales.map((supportedLocale: LocaleObject) => {
    const foundLocale = contentLocales.find(
      (locale) => locale.code === supportedLocale.code
    )
    const localeWithName = {
      ...supportedLocale,
      name: foundLocale?.name ?? supportedLocale.name,
    }
    return formattedLocaleObject(localeWithName, keyName, valueName)
  })
}

export const useCompanyLocale = createSharedComposable(() => {
  const { companySettings } = useCurrentCompanySettings()

  const companyLocale = computed(() => {
    return companySettings.value?.default_locale
  })

  const companyLocaleCode = computed(() => companyLocale.value?.code)

  const supportedLocales = computed(() => {
    const locales = companySettings.value?.supported_locales || []

    const sorted = [...locales].sort((a, b) => a.name.localeCompare(b.name))
    const defaultIndex = sorted.findIndex(
      (locale) => locale.code === companyLocale.value?.code
    )

    if (defaultIndex > 0) {
      const defaultLocale = sorted.splice(defaultIndex, 1)[0]
      sorted.unshift(defaultLocale)
    }

    return sorted
  })

  const contentLocaleDisplayOptions = computed(() => {
    return mapLocaleOptions(supportedLocales.value, 'display', 'value')
  })

  const contentLocaleOptions = computed(() => {
    return mapLocaleOptions(supportedLocales.value, 'name', 'code')
  })

  const getContentLocaleCodes = () => {
    return supportedLocales.value.map((locale) => locale.code)
  }

  return {
    companyLocale,
    companyLocaleCode,
    supportedLocales,
    contentLocaleDisplayOptions,
    contentLocaleOptions,
    getContentLocaleCodes,
  }
})
